import { CloudUpload } from '@mui/icons-material';
import { Avatar, Box, Button, FormControl, Typography } from '@mui/material';
import type { ChangeEvent } from 'react';
import { memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const ACCEPTED_FILE_TYPES = '.png,.svg';
const LOGO_DIMENSIONS = { width: 80, height: 80 } as const;
const LOGO_MAX_HEIGHT = 250;

export const LogoUpload = memo(
  ({
    logoImage,
    onLogoChange,
    disabled,
    error,
    headerColor,
    required = false,
  }: {
    logoImage?: string;
    onLogoChange: (e: ChangeEvent<HTMLInputElement>) => void;
    disabled: boolean;
    error?: string[];
    headerColor: string;
    required?: boolean;
  }) => {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    return (
      <FormControl fullWidth margin="normal">
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            border: '2px dashed',
            borderColor: error
              ? 'var(--color-dark-grey-red)'
              : logoImage
                ? 'var(--color-scifree-teal)'
                : 'var(--color-dark-greyish-blue)',
            backgroundColor: error
              ? 'var(--color-light-grey-red)'
              : 'var(--color-white)',
            transition: 'all 0.3s ease',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            {logoImage ? (
              <Box
                sx={{
                  mx: -3,
                  mt: -3,
                  mb: 2,
                  p: 3,
                  backgroundColor: headerColor || 'var(--color-white)',
                }}
              >
                <Box
                  component="img"
                  src={logoImage}
                  alt="Brand logo"
                  sx={{
                    maxHeight: LOGO_MAX_HEIGHT,
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: 1,
                    boxShadow: 2,
                  }}
                />
              </Box>
            ) : (
              <>
                <Avatar
                  sx={{
                    width: LOGO_DIMENSIONS.width,
                    height: LOGO_DIMENSIONS.height,
                    mx: 'auto',
                    mb: 2,
                    backgroundColor: 'var(--color-light-greyish-blue)',
                  }}
                >
                  <CloudUpload sx={{ fontSize: 32, color: 'grey.400' }} />
                </Avatar>
                <Typography variant="body2" gutterBottom>
                  {t('branding.messages.noLogoUploaded')}
                </Typography>
              </>
            )}

            <Box sx={{ display: 'inline-block' }}>
              <Button
                variant="default"
                startIcon={<CloudUpload />}
                disabled={disabled}
                size={logoImage ? 'small' : 'medium'}
                aria-label={t('branding.buttons.upload') as string}
                onClick={() => inputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (!disabled) inputRef.current?.click();
                  }
                }}
              >
                {t('branding.buttons.upload')}
              </Button>
              <input
                ref={inputRef}
                id="logo-upload-input"
                type="file"
                hidden
                accept={ACCEPTED_FILE_TYPES}
                onChange={onLogoChange}
                required={required}
                disabled={disabled}
                tabIndex={-1}
                aria-hidden
                aria-label="Logo upload"
              />
            </Box>
          </Box>
        </Box>
        {error && (
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            {error[0]}
          </Typography>
        )}
      </FormControl>
    );
  }
);

LogoUpload.displayName = 'LogoUpload';
